'use client'

import { useWatch } from 'react-hook-form'

import { useInstrumentMap } from '@/features/order/order-form/hooks/use-instrument-map'
import { useUnifiedOrderFormContext } from '@/features/order/order-form/lib/order-form-context'
import { planScaleRun, type ScaleRun } from '@/features/scale/lib/build-scale-params'

/**
 * The Scale run the form would submit right now, re-planned on every change to the
 * scale fields or the order rows — the preview and the submit gate both read it, and
 * the submitter calls the same planScaleRun. `run` is null unless there is exactly one
 * row with a positive amount, or while `active` is false (the form is on another tab).
 * Cheap (<= 50 levels), so no memo.
 */
export function useScaleRun(active = true) {
  const { form, primaryExchange, getSubmissionRows } = useUnifiedOrderFormContext()
  const instrumentMap = useInstrumentMap(primaryExchange)
  const [scaleStartPrice, scaleEndPrice, scaleOrderCount, scaleSizeRatio] = useWatch({
    control: form.control,
    name: ['scaleStartPrice', 'scaleEndPrice', 'scaleOrderCount', 'scaleSizeRatio'],
  })
  // Subscribes to the rows so the run follows amount/side/symbol edits.
  useWatch({ control: form.control, name: 'items' })

  const fields = { scaleStartPrice, scaleEndPrice, scaleOrderCount, scaleSizeRatio }
  const rows = active ? getSubmissionRows() : []
  const row = rows.length === 1 ? rows[0] : null
  const run: ScaleRun | null =
    row && row.rawItem.amount > 0
      ? planScaleRun(instrumentMap, row.item, row.rawItem.amount, fields)
      : null
  return {
    rowCount: rows.length,
    row,
    instrument: row ? instrumentMap.get(row.item.symbol) : undefined,
    run,
    fields,
  }
}
